<template>
  <v-navigation-drawer
    v-model="cartDrawer"
    app
    right
    temporary>
    <div class="cart-drawer">
      <div class="cart-drawer__header">
        <h4 class="cart-drawer__header-title title">
          cart
        </h4>
        <v-btn
          icon
          class="filter-drawer-close"
          @click.stop="closeCartDrawer">
          <v-icon>
            close
          </v-icon>
        </v-btn>
      </div>
      <v-divider />

      <div class="cart-drawer__items-wrapper">
        <!-- EMPTY CART -->
        <div
          v-if="cart.hats.length===0 && cart.patches.length===0 && cart.customPatches.length===0"
          class="cart-drawer__empty-cart">
          <v-icon class="cart-drawer__empty-cart-icon">
            sentiment_dissatisfied
          </v-icon>
          <span class="cart-drawer__empty-cart-text">There's nothing in your cart!</span>
          <v-btn
            flat
            color="accent"
            :to="{ path: '/hats' }">
            shop hats
          </v-btn>
          <v-btn
            flat
            color="accent"
            :to="{ path: '/patches/catalog' }">
            shop patches
          </v-btn>
          <v-btn
            flat
            color="accent"
            :to="{ path: '/custom' }">
            create your own
          </v-btn>
        </div>
        <!-- HATS -->
        <h5
          v-if="cart.hats.length > 0"
          class="cart-drawer__section-header">
          hats
        </h5>
        <ul class="cart-drawer__items">
          <cart-item
            v-for="hat in cart.hats"
            :key="hat.sku"
            :item="hat"
            item-set="hats" />
        </ul>

        <!-- PATCHES -->
        <h5
          v-if="cart.patches.length > 0"
          class="cart-drawer__section-header">
          patches
        </h5>
        <ul class="cart-drawer__items">
          <cart-item
            v-for="patch in catalogPatches"
            :key="patch.imageId"
            :item="patch"
            item-set="patches" />
          <cart-item
            v-for="patch in submissionPatches"
            :key="patch.imageId"
            :item="patch"
            item-set="patches" />
        </ul>

        <ul class="cart-drawer__items">
          <cart-item
            v-for="patch in savedCustoms"
            :key="patch.imageId"
            :item="patch"
            item-set="patches" />
          <cart-item
            v-for="customPatch in cart.customPatches"
            :key="customPatch.imageId"
            :item="customPatch"
            item-set="customPatches" />
        </ul>
      </div>

      <div class="cart-drawer__footer">
        <div
          v-if="cart.discount || cart.discount===0"
          class="cart-drawer__totals-row">
          <h5 class="totals-row__label">
            items total
          </h5>
          <h5 class="totals-row__amount">
            {{ itemsTotal | price }}
          </h5>
        </div>

        <div
          v-if="cart.discount || cart.discount===0"
          class="cart-drawer__totals-row">
          <h5 class="totals-row__label totals-row__label--discount">
            coupon
          </h5>
          <h5
            v-if="!loadingCoupon"
            class="totals-row__amount totals-row__amount--discount">
            -{{ cart.discount | price }}
          </h5>
          <v-progress-circular
            v-if="loadingCoupon"
            color="primary"
            indeterminate />
        </div>
        <div class="cart-drawer__subtotals-row">
          <h5 class="subtotals-row__label">
            subtotal
          </h5>
          <h5 class="subtotals-row__amount">
            {{ subtotal | price }}
          </h5>
        </div>
        <v-btn
          :to="{ name: 'cart' }"
          :disabled="this.$route.name === 'cart'"
          large
          outline
          color="primary"
          class="cart-drawer__view-cart">
          View Cart
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex';

import CartItem from './cart/CartItem.vue';

export default {
  name: 'CartDrawer',
  components: {
    CartItem,
  },
  computed: {
    cartDrawer: {
      get() {
        return this.$store.state.app.cartDrawer;
      },
      set(value) {
        this.$store.commit('app/setCartDrawer', value);
      },
    },
    ...mapState('cart', {
      loading: 'loading',
      loadingCoupon: 'loadingCoupon',
      couponDescription: 'couponDescription',
      couponErrorMessages: 'couponErrorMessages',
      cart: 'cart',
    }),

    catalogPatches() {
      return this.cart.patches.map(x => x).filter(patch => patch.patchGroup === 'catalog');
    },
    submissionPatches() {
      return this.cart.patches.map(x => x).filter(patch => patch.patchGroup === 'submissions');
    },
    savedCustoms() {
      return this.cart.patches.map(x => x).filter(patch => patch.patchGroup === 'savedCustoms');
    },

    itemsTotal() {
      return this.$store.getters['cart/cartItemsSubtotal'];
    },
    subtotal() {
      return this.$store.getters['cart/cartItemsTotal'];
    },
  },
  methods: {
    closeCartDrawer() {
      this.$store.dispatch('app/closeAllDrawers');
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.cart-drawer
  display: flex
  flex-flow: column nowrap
  align-items: stretch
  justify-content: space-between
  height: 100%
  padding-top: 3rem
  background-color: $white

  &__header
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    padding-left: $base-padding*2

  &__items-wrapper
    display: flex
    flex: 1 1 0
    flex-flow: column nowrap

  &__empty-cart
    display: flex
    flex-flow: column
    align-items: center
    padding: $base-padding
    color: $accent
    &-icon
      color: currentColor
      font-size: 4rem
    &-text
      padding-bottom: $base-padding
      border-bottom: 1px solid $accent

  &__section-header
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    padding-top: $base-padding
    padding-left: $base-padding*2
    font-size: 1rem

  &__items
    display: flex
    flex-flow: column nowrap
    align-items: stretch
    justify-content: flex-start
    padding-right: $base-padding
    padding-left: $base-padding*2
    list-style: none

  &__footer
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: space-between
    padding-right: $base-padding*2
    padding-left: $base-padding*2

  &__totals-row
  &__subtotals-row
    display: flex
    justify-content: space-between
    width: 100%
    &:first-of-type
      border-top: 1px solid $accent
    &:last-of-type
      border-bottom: 1px solid $accent

  &__view-cart
    width: 100%

  .totals-row__label,
  .totals-row__amount
    font-weight: $base-font-weight
    font-size: 1rem
    &--discount
      color: $primary
      font-style: italic
  .subtotals-row__label,
  .subtotals-row__amount
    font-weight: $base-header-font-weight
    font-size: 1rem

</style>
